import { useState } from "react";
import { Card } from "@/ui/card";
import { Spinner } from "@/ui/spinner";
import { useAcoes, useEstado } from "../estado.jsx";
import { Botao, Campo } from "./comuns.jsx";

/*
 * Entrada rápida: um clique, sem digitar.
 *
 * Quem decide se ela existe é o BUILD, não o código: a credencial vem de
 * `VITE_LOGIN_TESTE=email:senha` (e `VITE_LOGIN_TESTE_LOCAL` para o banco
 * local), e sem a variável o botão não é renderizado. Assim tirar o atalho é
 * apagar uma linha do `.env.local` e buildar de novo — não é caçar `if` no meio
 * da tela.
 *
 * A senha do banco local não é a do servidor, então são duas credenciais e a
 * escolha é pelo HOST da página: em `localhost`/`127.0.0.1` vale
 * `VITE_LOGIN_TESTE_LOCAL`; em qualquer outro endereço, `VITE_LOGIN_TESTE`.
 *
 * O preço, dito na cara e SEM ATENUANTE: a senha VIAJA NO PACOTE. Desde 30/08 o
 * CRM atende em `rocha101.duckdns.org`, que é a internet — então qualquer pessoa
 * que abra a página lê o bundle e entra como o dono daquela conta. O atalho ficou
 * no endereço público por decisão do escritório (30/08), com duas consequências
 * que não são opinião:
 *
 *   1. a conta de `VITE_LOGIN_TESTE` tem de ser DESCARTÁVEL — nunca a do sócio,
 *      nunca uma que alcance dinheiro, documento assinado ou acesso de gente;
 *   2. tirar a variável do `.env.local` e rebuildar é o único jeito de fechar,
 *      e a senha que esteve publicada precisa ser trocada depois.
 */
/* ponytail: host, não modo de build. Túnel que exponha o servidor em
   `localhost` pegaria a credencial errada — se isso virar rotina, a escolha
   passa a ser por porta ou por um marcador que a API devolva. */
const AQUI = typeof location !== "undefined" && (location.hostname === "localhost" || location.hostname === "127.0.0.1");
const TESTE = String((AQUI ? import.meta.env.VITE_LOGIN_TESTE_LOCAL : import.meta.env.VITE_LOGIN_TESTE) ?? "").split(":");
const LOGIN_TESTE = TESTE.length === 2 && TESTE[0] && TESTE[1] ? { email: TESTE[0], senha: TESTE[1] } : null;

/**
 * A porta. Sem sessão, é a única coisa que a mesa mostra.
 *
 * O cartão é o `Card` do shadcn e as caixas são o `Campo` da casa — que já é o
 * trio `Field`/`FieldLabel`/`Input`, com o rótulo amarrado ao campo. O `<form>`
 * mora DENTRO do cartão: o cartão é a moldura, o formulário é o que ele contém,
 * e `Card` só sabe desenhar um `<div>`.
 */
export default function Entrada() {
  const { erroLogin } = useEstado();
  const { entrar } = useAcoes();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  /* Entrar é uma ida ao servidor com o banco talvez ainda acordando: sem sinal
     nenhum, o segundo clique manda a mesma senha de novo e a única resposta que
     a tela dá é o erro, se houver. `entrar` não relança — quem falha escreve em
     `erroLogin` —, então o `finally` é o que devolve o botão. */
  const [enviando, setEnviando] = useState(false);
  async function enviar(mail, pass) {
    setEnviando(true);
    try {
      await entrar(mail, pass);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex h-dvh items-center justify-center bg-background p-5">
      <Card className="w-[min(380px,100%)] gap-0 rounded-[20px] border border-border p-[26px] shadow-[0_18px_44px_color-mix(in_oklab,_var(--color-foreground)_10%,_transparent)] ring-0 [--card-spacing:0px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enviar(email, senha);
          }}
        >
          {/* Cartão claro: aqui vale a marca navy. */}
          <img src="/fga-mark.png" alt="Galera Advocacia" className="mb-4 block h-[38px] w-auto" />
          <h1 className="m-0 mb-1 font-serif text-[27px] font-normal">Mesa de atendimento</h1>
          <p className="m-0 mb-5 text-corpo text-tinta-suave">Galera Advocacia — entre para continuar.</p>
          <Campo
            rotulo="E-mail"
            dica="e-mail"
            tipo="email"
            autoPreencher="username"
            valor={email}
            aoMudar={setEmail}
            estilo="margin-bottom:9px"
          />
          <Campo rotulo="Senha" dica="senha" tipo="password" autoPreencher="current-password" valor={senha} aoMudar={setSenha} />
          {/* `role="alert"`: a recusa da entrada é a única resposta desta tela, e sem
              isso ela acontecia em silêncio para quem usa leitor de tela. */}
          {erroLogin && (
            <div role="alert" className="mt-2.5 text-corpo leading-[1.45] text-primary">
              {erroLogin}
            </div>
          )}
          <Botao
            rotulo={
              enviando ? (
                <>
                  <Spinner className="size-3.5" />
                  Entrando…
                </>
              ) : (
                "Entrar"
              )
            }
            tipo="submit"
            destaque
            desabilitado={enviando}
            estilo="width:100%;margin-top:16px;font-size:14px;padding:13px;border-radius:12px"
          />
          {LOGIN_TESTE ? (
            <>
              <Botao
                rotulo={`Entrar como ${LOGIN_TESTE.email} (teste)`}
                /* Sem dica: o rótulo já diz tudo que a pessoa precisa aqui, e a
                   frase sobre `VITE_LOGIN_TESTE` era recado para quem configura
                   o servidor — na tela de entrar, onde qualquer um chega. */
                desabilitado={enviando}
                aoTocar={() => {
                  /* Preenche os campos também: quando a senha do .env está velha, o
                     erro aparece com o e-mail à vista em vez de num formulário em
                     branco que ninguém sabe o que tentou. */
                  setEmail(LOGIN_TESTE.email);
                  setSenha(LOGIN_TESTE.senha);
                  enviar(LOGIN_TESTE.email, LOGIN_TESTE.senha);
                }}
                estilo="width:100%;margin-top:8px;font-size:14px;padding:10px;border-radius:12px"
              />
              <div className="mt-[7px] text-apoio leading-[1.45] text-muted-foreground">
                Atalho de teste: a senha desta conta vem no pacote desta página, e qualquer pessoa que abra este endereço consegue
                lê-la. Use só com conta descartável.
              </div>
            </>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
